import React, { useCallback, useState } from 'react';
import { css, StyleSheet } from 'aphrodite/no-important';
import { ChevronIcon, Directions } from '../ChevronIcon';
import { DropDownMenu } from '../DropDownMenu';
import { useTheme } from '../../../../../theming/useTheme';
import { ErrorBoundary } from '../../../../errorHandling/ErrorBoundary';
import { MEDIA_BREAKPOINTS } from '../../../../../mediaQueries.const';

const componentConfigThemeKey = 'editions';

export interface EditionDataProps {
  text: string
  href: string
}

export interface EditionsThemeProps {
  color: string
}

export interface EditionsDataProps {
  editions: EditionDataProps[]
  editionEndpoint: string
}

export type EditionsProps = EditionsDataProps & EditionsThemeProps;

const getEditionsStyle = (color: string) => {
  return StyleSheet.create({
    editionsWrapper: {
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      position: 'relative',
      [MEDIA_BREAKPOINTS.MEDIUM]: {
        display: 'none',
      },
      [MEDIA_BREAKPOINTS.SMALL]: {
        display: 'none',
      },
    },
    editions: {
      alignItems: 'center',
      display: 'flex',
      justifyContent: 'space-between',
      width: '144px',
      color,
      textDecoration: 'none',
      backgroundColor: 'transparent',
      border: 'none',
      outline: 'none',
      ':hover': {
        cursor: 'pointer',
      },
    },
  });
};

export const getActiveEdition = (editions: EditionDataProps[], editionEndpoint:string): EditionDataProps => {
  return editions.find(edition => edition.href === editionEndpoint) || editions[0];
};

export const EditionsComponent:React.FunctionComponent<EditionsProps> = props => {
  const { editions, color, editionEndpoint } = props;
  const style = getEditionsStyle(color);
  const activeEdition = getActiveEdition(editions, editionEndpoint);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const editionsClickHandler = useCallback(() => {
    setIsMenuOpen(!isMenuOpen);
  }, [isMenuOpen]);

  return (
    <div className={css(style.editionsWrapper)}>
      <button type="button" className={css(style.editions)} onClick={editionsClickHandler}>
        <h4>{activeEdition.text}</h4>
        <ChevronIcon direction={isMenuOpen ? Directions.UP : Directions.DOWN} color={color} />
      </button>
      {isMenuOpen && <DropDownMenu menuLinks={editions} configThemeKey="editionsMenu" activeEndpoint={editionEndpoint} />}
    </div>
  );
};

const mapThemeProps = (theme: EditionsThemeProps) => {
  return {
    color: theme.color,
  };
};

export const Editions = (props: EditionsDataProps) => {
  const themeContext = useTheme(componentConfigThemeKey, mapThemeProps);
  return <ErrorBoundary><EditionsComponent {...themeContext} {...props} /></ErrorBoundary>;
};
